import _filter from 'lodash/filter'
import _isEmpty from 'lodash/isEmpty'
import _findIndex from 'lodash/findIndex'

/**
 * CSS selector for all focusable elements
 * Used for both initial focus and focus trapping
 */
export const FOCUS_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'iframe',
  'select:not([disabled])',
  'button:not([disabled])',
  'details',
  'textarea:not([disabled])',
  '[contentEditable=""]',
  '[contentEditable="true"]',
  '[contentEditable="TRUE"]',
  '[tabindex]:not([tabindex^="-"])',
].join(', ')

/**
 * Core focus navigation logic for modal/dialog focus trapping
 * @param {Event} event - The keyboard event
 * @param {HTMLElement} containerElement - The container element to trap focus within
 * @returns {boolean} - Whether the event was handled
 */
function handleFocusNavigation (event, containerElement) {
  // Only handle Tab key
  if (event.key !== 'Tab') return false

  if (!containerElement) return false

  // Check if the currently focused element is within the container
  const isWithinContainer = containerElement.contains(document.activeElement)
  if (!isWithinContainer) return false

  // Get all focusable elements within the container
  const allFocusableElements = Array.from(containerElement.querySelectorAll(FOCUS_SELECTOR))

  // Filter out disabled elements
  const filteredElements = _filter(allFocusableElements, (element) => (
    !element.disabled
    && !element.classList.contains('button--disabled')
    && element.offsetParent !== null // element is visible
  ))

  // Sort elements by tabIndex to ensure proper tab order
  const focusableElements = filteredElements.sort((a, b) => {
    const aTabIndex = parseInt(a.getAttribute('tabindex') || '0', 10)
    const bTabIndex = parseInt(b.getAttribute('tabindex') || '0', 10)
    return aTabIndex - bTabIndex
  })

  if (_isEmpty(focusableElements)) return false

  const firstFocusableElement = focusableElements[0]
  const lastFocusableElement = focusableElements[focusableElements.length - 1]
  const currentIndex = _findIndex(
    focusableElements,
    (element) => element === document.activeElement,
  )

  // If current element is not found in focusable elements, start from first element
  if (currentIndex === -1) {
    event.preventDefault()
    firstFocusableElement.focus()
    return true
  }

  // Always prevent default Tab behavior to keep focus within container
  event.preventDefault()

  if (event.shiftKey) {
    // Shift + Tab (backwards)
    if (currentIndex <= 0) {
      lastFocusableElement.focus()
    } else {
      focusableElements[currentIndex - 1].focus()
    }
  } else if (currentIndex >= focusableElements.length - 1) {
    // Tab (forwards) - wrap to first element
    firstFocusableElement.focus()
  } else {
    // Tab (forwards) - go to next element
    focusableElements[currentIndex + 1].focus()
  }

  return true
}

export { handleFocusNavigation }
